/**
 * Máquina de estados de booking — producción.
 * Transiciones permitidas + merge seguro (no pisar mapas concurrentes).
 */

export const BOOKING_STATUS = {
  PAYMENT_PENDING: 'PAYMENT_PENDING',
  NEW: 'NEW',
  ACCEPTED: 'ACCEPTED',
  PICKUP_USER: 'PICKUP_USER',
  ARRIVED: 'ARRIVED',
  STARTED: 'STARTED',
  REACHED: 'REACHED',
  PENDING: 'PENDING',
  PAID: 'PAID',
  COMPLETE: 'COMPLETE',
  CANCELLED: 'CANCELLED',
};

/** fromStatus -> toStatus[] */
export const ALLOWED_TRANSITIONS = {
  PAYMENT_PENDING: ['NEW', 'CANCELLED'],
  NEW: ['ACCEPTED', 'CANCELLED', 'PAYMENT_PENDING'],
  ACCEPTED: ['PICKUP_USER', 'ARRIVED', 'CANCELLED'],
  PICKUP_USER: ['ARRIVED', 'CANCELLED'],
  ARRIVED: ['STARTED', 'CANCELLED'],
  STARTED: ['REACHED', 'CANCELLED'],
  REACHED: ['PENDING', 'PAID', 'COMPLETE'],
  PENDING: ['PAID', 'COMPLETE'],
  PAID: ['COMPLETE'],
  COMPLETE: [],
  CANCELLED: [],
};

/** Claves anidadas que NUNCA se deben reemplazar con un merge completo del cliente */
export const PROTECTED_NESTED_KEYS = [
  'driverOffers',
  'requestedDrivers',
  'preRequestedDrivers',
  'driverEstimates',
];

export function canTransition(fromStatus, toStatus) {
  if (!fromStatus || !toStatus) return false;
  if (fromStatus === toStatus) return true;
  const allowed = ALLOWED_TRANSITIONS[fromStatus];
  if (!allowed) return false;
  return allowed.indexOf(toStatus) !== -1;
}

/**
 * Fusiona patch sobre current sin borrar mapas concurrentes
 * salvo que el patch los ponga explícitamente (incl. null para borrar).
 */
export function mergeBookingSafe(current, patch) {
  if (!current) return null;
  if (!patch || typeof patch !== 'object') return { ...current };

  const next = { ...current };
  const keys = Object.keys(patch);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (key === 'id') continue;
    if (PROTECTED_NESTED_KEYS.indexOf(key) !== -1) {
      if (Object.prototype.hasOwnProperty.call(patch, key)) {
        next[key] = patch[key];
      }
      continue;
    }
    next[key] = patch[key];
  }
  return next;
}

/**
 * Aplica transición de status + patch. Devuelve null si es ilegal (abort transaction).
 */
export function applyStatusTransition(current, toStatus, patch) {
  if (!current) return null;
  const from = current.status;
  if (!canTransition(from, toStatus)) {
    return null;
  }
  const merged = mergeBookingSafe(current, patch || {});
  merged.status = toStatus;
  return merged;
}

/** Campos de pago / cierre que el cliente puede escribir en un patch */
export const PAYMENT_PATCH_KEYS = [
  'prepaid',
  'payment_mode',
  'customer_paid',
  'discount',
  'usedWalletMoney',
  'cardPaymentAmount',
  'cashPaymentAmount',
  'payableAmount',
  'promo_applied',
  'promo_details',
  'selectedBid',
  'driver',
  'driver_image',
  'driver_name',
  'driver_contact',
  'driver_token',
  'car_image',
  'vehicle_number',
  'vehicleModel',
  'vehicleMake',
  'driverRating',
  'fleetCommission',
  'fleetadmin',
  'driver_share',
  'driverDeviceId',
  'trip_cost',
  'estimate',
  'reference',
  'gateway',
  'transaction_id',
  'paymentPacket',
];

export function pickPaymentPatch(booking) {
  const patch = {};
  if (!booking) return patch;
  for (let i = 0; i < PAYMENT_PATCH_KEYS.length; i++) {
    const k = PAYMENT_PATCH_KEYS[i];
    if (booking[k] !== undefined) {
      patch[k] = booking[k];
    }
  }
  return patch;
}

const ACTIVE_TRIP_STATUSES = [
  'ACCEPTED',
  'PICKUP_USER',
  'ARRIVED',
  'STARTED',
  'REACHED',
  'PENDING',
];

export function hasSettledPayment(booking) {
  if (!booking) return false;
  if (booking.driver_wallet_credited) return true;
  const paid = parseFloat(booking.customer_paid);
  const cash = parseFloat(booking.cashPaymentAmount);
  const card = parseFloat(booking.cardPaymentAmount);
  return (Number.isFinite(paid) && paid > 0)
    || (Number.isFinite(cash) && cash > 0)
    || (Number.isFinite(card) && card > 0);
}

/** Efectivo / Nequi / transferencia: el conductor confirma en PaymentDetails (REACHED → PAID). */
export function isOnArrivalPaymentMode(booking) {
  const mode = String(booking?.payment_mode || '').toLowerCase();
  return mode === 'cash' || mode === 'nequi' || mode === 'bank';
}

/**
 * Viajes con cierre real (endTime) o ya en REACHED/PENDING con pago,
 * pero status atrasado (race UI optimista).
 *
 * NO auto-PAID en cash/nequi/bank: al poner endTime con REACHED el heal
 * saltaba la pantalla de pago del conductor.
 * NO usar cashPaymentAmount solo en modos on-arrival (puede ir prellenado).
 */
export function resolveHealedTripStatus(booking) {
  if (!booking || !booking.status) return null;
  const status = booking.status;
  if (ACTIVE_TRIP_STATUSES.indexOf(status) === -1) return null;
  if (!booking.driver) return null;

  const start = Number(booking.startTime) || 0;
  const end = Number(booking.endTime) || 0;
  const onArrivalPay = isOnArrivalPaymentMode(booking);
  const settled = hasSettledPayment(booking);
  const canAutoPaid = settled && !onArrivalPay;
  const tripFinished = start > 0 && end > 0 && end >= start;
  const atPaymentGate = status === 'REACHED' || status === 'PENDING';

  if (tripFinished) {
    if (canAutoPaid) return 'PAID';
    // Cash/Nequi/Bank (u otros sin liquidar): llevar a puerta de pago, nunca cerrar solos.
    if (!atPaymentGate) return 'REACHED';
    return null;
  }

  if (atPaymentGate && canAutoPaid) return 'PAID';
  return null;
}
